import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.cam';

interface ArticleSection {
  type: 'heading' | 'subheading' | 'text' | 'insight' | 'warning' | 'cta';
  heading?: string;
  content?: string;
  items?: string[];
}

interface ArticleTemplateProps {
  title: string;
  description: string;
  datePublished: string;
  readTime: string;
  category: string;
  imageSrc: string;
  imageAlt: string;
  sections: ArticleSection[];
  slug: string;
}

const articleSchema = (props: ArticleTemplateProps) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": props.title,
  "description": props.description,
  "author": {
    "@type": "Organization",
    "name": "Vigorbuy Spreadsheet Hub"
  },
  "datePublished": props.datePublished,
  "dateModified": props.datePublished,
  "publisher": {
    "@type": "Organization",
    "name": "Vigorbuy Spreadsheet Hub",
    "url": siteUrl + "/"
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": siteUrl + "/" + props.slug
  }
});

const breadcrumbSchema = (props: ArticleTemplateProps) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": siteUrl + "/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": props.title,
      "item": siteUrl + "/" + props.slug
    }
  ]
});

export default function ArticleTemplate(props: ArticleTemplateProps) {
  const { title, description, datePublished, readTime, category, imageSrc, imageAlt, sections, slug } = props;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema(props)) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema(props)) }}
      />

      <header className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
        <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-white text-lg"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-950 whitespace-nowrap">
              Vigorbuy Spreadsheet
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 rounded-md bg-background-100 border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1"></i> Back to Home
            </Link>
          </div>
        </nav>
      </header>

      <article className="pt-24 pb-16 md:pb-24 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <nav className="text-xs text-foreground-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-600">{title}</span>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
              {category}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight text-balance">
              {title}
            </h1>
            <p className="text-foreground-500 text-sm">{datePublished} &middot; {readTime}</p>
          </header>

          <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
            <img
              src={imageSrc}
              alt={imageAlt}
              title={title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom">
            <p className="text-foreground-700 leading-relaxed mb-6">
              {description}
            </p>

            {sections.map((section, i) => {
              if (section.type === 'heading') {
                return (
                  <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                    {section.heading}
                  </h2>
                );
              }

              if (section.type === 'subheading') {
                return (
                  <h3 key={i} className="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-2">
                    {section.heading}
                  </h3>
                );
              }

              if (section.type === 'insight') {
                return (
                  <div key={i} className="bg-accent-50 border border-accent-200 rounded-xl p-5 my-8">
                    <p className="text-sm text-accent-800 font-medium mb-2 flex items-center gap-2">
                      <i className="ri-lightbulb-line"></i> Key Insight
                    </p>
                    <p className="text-sm text-accent-700 leading-relaxed">{section.content}</p>
                  </div>
                );
              }

              if (section.type === 'warning') {
                return (
                  <div key={i} className="bg-primary-50 border border-primary-200 rounded-xl p-5 my-8">
                    <p className="text-sm text-primary-800 font-medium mb-2 flex items-center gap-2">
                      <i className="ri-shield-check-line"></i> Safety Reminder
                    </p>
                    <p className="text-sm text-primary-700 leading-relaxed">{section.content}</p>
                    {section.items && (
                      <ul className="mt-3 space-y-1.5">
                        {section.items.map((item, j) => (
                          <li key={j} className="text-sm text-primary-700 flex items-start gap-2">
                            <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }

              if (section.type === 'cta') {
                return (
                  <div key={i} className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <p className="font-heading text-lg font-semibold text-foreground-950 mb-2">{section.heading}</p>
                    <p className="text-sm text-foreground-600 mb-4">{section.content}</p>
                    <div className="flex flex-wrap gap-3">
                      <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-line"></i> Back to Home
                      </Link>
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
                      >
                        Visit Main Website <i className="ri-external-link-line"></i>
                      </a>
                    </div>
                  </div>
                );
              }

              if (section.type === 'text') {
                return (
                  <p key={i} className="text-foreground-700 leading-relaxed mb-4">{section.content}</p>
                );
              }

              return null;
            })}
          </div>
        </div>
      </article>

      <footer className="bg-background-950 text-foreground-50 py-10 px-4 md:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-500 text-xs">
            &copy; {new Date().getFullYear()} Vigorbuy Spreadsheet Hub. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-foreground-400 text-xs hover:text-white transition-colors whitespace-nowrap">Home</Link>
            <Link to="/vigorbuy-spreadsheet-guide" className="text-foreground-400 text-xs hover:text-white transition-colors whitespace-nowrap">Shopping Guide</Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-foreground-400 text-xs hover:text-white transition-colors whitespace-nowrap"
            >
              Main Website
            </a>
          </div>
        </div>
      </footer>
    </>
  );
}